"use client";

import { Fragment, useEffect, useState } from "react";
import { useRouter } from "@/hooks/useCompatRouter";

import Body from "@/containers/category/Body";
import Top from "@/containers/category/Top";
import useProductsFilter from "@/hooks/useProductsFilter";
import {
  getPriceMinMaxByFilter,
  getProductsByFilter,
} from "@/requests/product";
import { currentPageQuery, routes } from "@/utils/constants";

export interface BrandViewProps {
  brand: BrandItem;
  builderPage: BuilderPage | null;
  products: ProductItemV2[];
  lowestPrice: number;
  highestPrice: number;
  totalProducts: number;
}

const BrandView = ({
  brand,
  products: _products,
  totalProducts: _totalProducts,
  lowestPrice: _productsLowestPrice,
  highestPrice: _productsHighestPrice,
  builderPage,
}: BrandViewProps) => {
  const router = useRouter();

  const productsFilter = useProductsFilter(builderPage?.widgets);

  const [products, setProducts] = useState<ProductItemV2[]>(_products || []);
  const [lowestPrice, setLowestPrice] = useState<number>(
    _productsLowestPrice || 0,
  );
  const [highestPrice, setHighestPrice] = useState<number>(
    _productsHighestPrice || 0,
  );
  const [totalProducts, setTotalProducts] = useState<number>(
    _totalProducts || 0,
  );

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    const timeout = setTimeout(() => {
      const {
        slug,
        [currentPageQuery]: currentPage,
        ...restQuery
      } = router.query;
      if (router.query.slug) {
        getProductsByFilter({
          currentPage: +(currentPage ?? 1),
          perPage: productsFilter.perPage || 20,
          filter: `&brands=${router.query.slug}`,
          query: restQuery,
        })
          .then((res) => {
            setProducts(res.data);
            setTotalProducts(res.totalResult);
          })
          .finally(() => {
            setLoading(false);
          });

        getPriceMinMaxByFilter({
          currentPage: productsFilter.activePage,
          perPage: productsFilter.perPage || 20,
          // was `&category=` — copy-paste bug from the old brand page; the
          // price bounds were global instead of scoped to this brand
          filter: `&brands=${router.query.slug}`,
          query: restQuery,
        })
          .then((res) => {
            setLowestPrice(res.lowestPrice);
            setHighestPrice(res.highestPrice);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }, 150);

    return () => {
      clearTimeout(timeout);
    };
  }, [router.query, productsFilter.perPage, productsFilter.activePage]);

  return (
    <Fragment key={brand.slug?.toString() || ""}>
      <Top
        brands={[]}
        onFilter={productsFilter.handleFilter}
        breadcrumbs={[
          { name: "Home", link: { urlType: "custom", url: "/" } },
          {
            name: "Brand",
            link: { urlType: "custom", url: routes.brand },
          },
          {
            name: brand.name,
            link: {
              urlType: "custom",
              url: `${routes.brand}${brand.slug}`,
            },
          },
        ]}
        productsFilter={productsFilter}
        widgets={builderPage?.topbar || []}
      />

      <Body
        skipAttributes
        products={products}
        lowestPrice={lowestPrice}
        highestPrice={highestPrice}
        category={brand}
        brandSlug={brand.slug}
        onFilter={productsFilter.handleFilter}
        onUrlFilter={productsFilter.handleFilterByURL}
        total={totalProducts}
        loading={loading}
        productsFilter={productsFilter}
        setActivePage={productsFilter.setActivePage}
        widgets={builderPage?.widgets || []}
        setPerPage={productsFilter.setPerPage}
        perPage={productsFilter.perPage}
      />
    </Fragment>
  );
};

export default BrandView;
